import React, { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, Zap } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { MultiSelect } from "@/components/kit/MultiSelect";

// Mass stream generation: multi offers x multi buyers, names auto-generated.
export function BulkGenerateDialog({ open, onOpenChange, offerOptions, buyerOptions }) {
  const qc = useQueryClient();
  const [offerIds, setOfferIds] = useState([]);
  const [buyerIds, setBuyerIds] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setOfferIds([]); setBuyerIds([]); } }, [open]);

  const pairs = offerIds.length * buyerIds.length;

  const run = async () => {
    if (!pairs) return toast.error("Select at least one offer and one buyer");
    setBusy(true);
    try {
      const { data } = await http.post("/streams/bulk-generate", { offer_ids: offerIds, buyer_ids: buyerIds });
      toast.success(`Created ${data.created} stream${data.created === 1 ? "" : "s"}`, {
        description: data.skipped ? `${data.skipped} existing offer–buyer pair(s) skipped.` : "Names and stream keys were generated automatically.",
      });
      qc.invalidateQueries({ queryKey: ["streams"] });
      qc.invalidateQueries({ queryKey: ["nav-counters"] });
      onOpenChange(false);
    } catch (e) {
      toast.error("Generation failed", { description: e?.response?.data?.detail || "Try again." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-white/[0.1] bg-[#141417] sm:max-w-md" data-testid="bulk-generate-dialog">
        <DialogHeader>
          <DialogTitle className="text-foreground">Generate Streams</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            One stream is created for every offer × buyer pair. Names are generated automatically; existing pairs are skipped.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">GEO offers</label>
            <MultiSelect values={offerIds} onChange={setOfferIds} options={offerOptions}
              label="Select offers" searchPlaceholder="Search offers…" testId="bulkgen-offers" widthClass="w-[360px]" triggerClass="h-10" />
          </div>
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">Users (buyers, managers, admins)</label>
            <MultiSelect values={buyerIds} onChange={setBuyerIds} options={buyerOptions}
              label="Select users" searchPlaceholder="Search users…" testId="bulkgen-buyers" widthClass="w-[360px]" triggerClass="h-10" />
          </div>
          {pairs > 0 && (
            <p className="text-xs text-muted-foreground" data-testid="bulkgen-summary">
              {offerIds.length} offer{offerIds.length === 1 ? "" : "s"} × {buyerIds.length} user{buyerIds.length === 1 ? "" : "s"} → up to <span className="text-foreground">{pairs}</span> stream{pairs === 1 ? "" : "s"}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-muted-foreground">Cancel</Button>
          <Button onClick={run} disabled={busy || !pairs} data-testid="bulkgen-confirm" className="gap-1.5 bg-white text-black hover:bg-white/90">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />} Generate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
